import { Button } from "@/components/ui/button"
import { useFibe, useGetLoanProviders } from "@/hooks/use-loan"
import { Loader2 } from "lucide-react"
import { usePayment } from "../use-payment"
import { toast } from "sonner"
import { useFormStore } from "@/stores/use-form-store"
import { useMemo } from "react"
import { disabledStatuses, getPeriodType, getPlanType } from "../utils"
import { PaymentStatus } from "@/types/form-types"
import type { UseFormReturn } from "react-hook-form"
import { logger } from "@/utils/logger"
// import { getPeriodType } from "../utils"
// import { useMemo } from "react"

type Props = {
	form: UseFormReturn
	status: PaymentStatus
	periodNumber: number
}

const LoanPaymentActions = ({ form, periodNumber }: Props) => {
	const {
		loanProviderForm,
		feeSummary,
		selectedPaymentPlan,
		fieldConfig,
		// applicatioDiscount,
	} = usePayment()
	const { applicationId, config, isPreview } = useFormStore()
	const { useApplyLoan } = useFibe()
	const { mutate: applyLoan, isPending } = useApplyLoan()
	const {
		course_id,
		// applicatioDiscount,
		activeLoanPlans,
		getValues,
		couponDiscount,
		// formState,
	} = usePayment()
	const { final_payable_amount, stage_check_in_time } = getValues()

	// Get loan providers data for the course
	const { data: loanProviders } = useGetLoanProviders({
		course_id: course_id || null,
	})

	// Get the selected lender's details
	const selectedLender = loanProviders?.find(
		provider =>
			provider.lender_id ===
			loanProviderForm.form.getValues("loan_provider_id"),
	)

	// const selectedOrderItems = useMemo(() => {
	// 	console.log("[NEW]fee summary: ", feeSummary)
	// 	if (!feeSummary) return []

	// 	return feeSummary.map(item => {
	// 		return {
	// 			fee_component_id: item.component_id,
	// 			component_dimension_id: item.dimension_id,
	// 			amount: item.finalPrice || 0,
	// 			currency: item.currency,
	// 			period_type: getPeriodType(item.frequency),
	// 			period_number: item.period_number,
	// 		}
	// 	})
	// }, [feeSummary])

	const adjustments = useMemo(() => {
		if (
			!couponDiscount ||
			couponDiscount.discount === 0 ||
			couponDiscount.payment_mode !== "LOAN"
		)
			return []

		const fee = feeSummary.find(
			fee => fee.coupon_discount && fee.coupon_discount > 0,
		)

		if (!fee || !fee.coupon_discount || fee.coupon_discount === 0) return []

		const temp = [
			// {
			// 	...applicatioDiscount,
			// 	amount: applicatioDiscount.discount,
			// },

			{
				amount: fee.coupon_discount,
				currency: fee.currency,
				type: "DISCOUNT",
				applied_by: couponDiscount.applied_by,
				applied_at: stage_check_in_time,
				description: `{${couponDiscount.coupon_id}}-coupon applied`,
			},
		]
		return temp.map(item => {
			return {
				amount: item.amount,
				currency: item.currency,
				// type: getPlanType(
				// 	activeLoanPlans.find(
				// 		plan =>
				// 			plan.component_id ===
				// 			selectedPaymentPlan.replace(/^(self|loan)-/, ""),
				// 	)?.frequency || "",
				// ),
				type: item.type,
				applied_by: item.applied_by,
				applied_at: item.applied_at,
				description: item.description,
			}
		})
	}, [couponDiscount, feeSummary])

	const selectedOrderItems = feeSummary.flatMap(item => {
		if (item.isDiscountComponent) return []
		return item.price_details?.map(price => {
			return {
				fee_component_id: item.component_id,
				component_dimension_id: item.dimension_id,
				amount: price.final_price,
				period_type: getPeriodType(item.frequency),
				period_number: price.period_number,
				currency: item.currency,
			}
		})
	})

	const paymentStatus = form.watch(fieldConfig?.field_id + ".payment_status")

	const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		const isValid = await loanProviderForm.form.trigger()
		logger.info("isValid Form : ", isValid)
		logger.info(
			"formstate errors : ",
			loanProviderForm.form.formState.errors,
		)
		if (!isValid) {
			toast.error("Please fill all the required fields")
			return
		}

		applyLoan({
			lender: loanProviderForm.form.getValues("loan_provider_name"),
			lender_id: loanProviderForm.form.getValues("loan_provider_id"),
			additional_details: {
				plan_id: selectedPaymentPlan,
			},
			applicant_info: {
				first_name: loanProviderForm.form.getValues(
					"applicant_first_name",
				),
				last_name: loanProviderForm.form.getValues(
					"applicant_last_name",
				),
				full_name: loanProviderForm.form.getValues(
					"applicant_full_name",
				),
				email: loanProviderForm.form.getValues("applicant_email"),
				country_code: "+91",
				phone: loanProviderForm.form.getValues(
					"applicant_phone_number",
				),
				dob: loanProviderForm.form.getValues("applicant_dob"),
				relationship:
					loanProviderForm.form.getValues("applicant_relation"),
				pan_number: loanProviderForm.form.getValues(
					"applicant_pan_number",
				),
				pincode: loanProviderForm.form.getValues("applicant_pincode"),
				marital_status: loanProviderForm.form.getValues(
					"applicant_marital_status",
				),
				employment_type: loanProviderForm.form.getValues(
					"applicant_employment_type",
				),
				monthly_income: loanProviderForm.form
					.getValues("applicant_monthly_income")
					.toString(),
			},
			student_info: {
				dob: new Date(loanProviderForm.form.getValues("student_dob")),
				first_name:
					loanProviderForm.form.getValues("student_first_name"),
				full_name: "",
				last_name: loanProviderForm.form.getValues("student_last_name"),
			},
			application_id: applicationId || "",
			batch_id: config?.batch_id || "",
			course_info: {
				batch_lender_course_id:
					selectedLender?.tenures?.find(
						tenure =>
							tenure.tenure_in_months ===
							loanProviderForm.form.getValues(
								"loan_provider_tenure",
							),
					)?.batch_lender_course_id || "",
				course_id: course_id,
			},
			currency: feeSummary[0]?.currency || "INR",
			fee_info: {
				adjustments: adjustments,
				fee_grid_revision_id: feeSummary[0]?.revision_id,
				items: selectedOrderItems,
				semester: periodNumber || 1,
				plan: getPlanType(
					activeLoanPlans?.find(
						plan =>
							plan.component_id ===
							selectedPaymentPlan.replace(/^(self|loan)-/, ""),
					)?.frequency || "",
				),
			},
			loan_info: {
				// Take out the interest rate from the selected lender tenure
				interest_rate:
					loanProviders
						?.find(
							provider =>
								provider.lender_id ===
								loanProviderForm.form.getValues(
									"loan_provider_id",
								),
						)
						?.tenures?.find(
							tenure =>
								tenure.tenure_in_months ===
								loanProviderForm.form.getValues(
									"loan_provider_tenure",
								),
						)?.interest_rate || 0,
				tenure_months: loanProviderForm.form.getValues(
					"loan_provider_tenure",
				),
				loan_amount: final_payable_amount,
				loan_type: selectedLender?.loan_type || "",
				merchant_id: selectedLender?.merchant_id || "",
				lender_course_id:
					selectedLender?.tenures?.find(
						tenure =>
							tenure.tenure_in_months ===
							loanProviderForm.form.getValues(
								"loan_provider_tenure",
							),
					)?.lender_course_id || "",
				lender_plan_id:
					loanProviders
						?.find(
							provider =>
								provider.lender_id ===
								loanProviderForm.form.getValues(
									"loan_provider_id",
								),
						)
						?.tenures?.find(
							tenure =>
								tenure.tenure_in_months ===
								loanProviderForm.form.getValues(
									"loan_provider_tenure",
								),
						)?.lender_plan_id || "",
			},
			partner_id: config?.partner_id || "",
			created_at: stage_check_in_time,
		})
	}
	return (
		<Button
			onClick={handleSubmit}
			type="button"
			disabled={
				isPending ||
				disabledStatuses.includes(paymentStatus) ||
				isPreview
			}
			className="mt-9 flex w-full items-center justify-center gap-2 rounded-2xl bg-green-600 text-white hover:bg-green-500"
		>
			<svg
				width="20"
				viewBox="0 0 48 48"
				xmlns="http://www.w3.org/2000/svg"
			>
				<g
					fill="none"
					stroke="currentColor"
					strokeWidth="4"
					strokeLinejoin="round"
				>
					<path d="M4 10a2 2 0 0 1 2-2h36a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10z" />
					<path
						d="M4 16h40"
						strokeLinecap="square"
					/>
					<path
						d="M27 32h9"
						strokeLinecap="round"
					/>
					<path
						d="M44 10v16"
						strokeLinecap="round"
					/>
					<path
						d="M4 10v16"
						strokeLinecap="round"
					/>
				</g>
			</svg>
			<span className="flex items-center justify-center gap-2">
				<span>Apply Loan</span>
				{isPending && <Loader2 className="animate-spin" />}
			</span>
		</Button>
	)
}

export default LoanPaymentActions
